import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const MissingNumber: React.FC = () => {
    const [sequence, setSequence] = useState<number[]>([]);
    const [missingNumber, setMissingNumber] = useState<number | null>(null);
    const [options, setOptions] = useState<number[]>([]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Missing Number Worksheet – Nursery Math Practice | Mama’s Guidance"; // Set page title
    }, []);

    const generateNewQuestion = () => {
        const start = Math.floor(Math.random() * 10) + 1; // Random starting number (1-10)
        const fullSequence = Array.from({ length: 5 }, (_, i) => start + i); // Create a sequence of 5 numbers
        const missingIndex = Math.floor(Math.random() * 5); // Choose a random position to hide
        const newMissingNumber = fullSequence[missingIndex];

        // Remove the missing number
        const newSequence = [...fullSequence];
        newSequence[missingIndex] = -1; // Placeholder for missing number

        // Generate answer options (correct + random numbers)
        const newOptions = new Set<number>([newMissingNumber]);
        newOptions.add(fullSequence[missingIndex] + 1)
        newOptions.add(fullSequence[missingIndex] + 2)
        newOptions.add(fullSequence[missingIndex] - 1)

        //console.log(newOptions)

        setSequence(newSequence);
        setMissingNumber(newMissingNumber);
        setOptions(Array.from(newOptions).sort(() => Math.random() - 0.5)); // Shuffle options
        setMessage("");
    };

    const handleAnswerClick = (selected: number) => {
        if (selected === missingNumber) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion();
                setShowImage(false);
                setMessage('')
            }, 5000); // Delay reduced to 3 seconds
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Boost early math skills with the "Missing Number" activity on Mamas Guidance—designed to help nursery kids recognize number patterns and sequences by identifying the missing numbers in a fun and interactive way.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord matchImage letterOption match-container" style={{ textAlign: "center", padding: "20px" }}>
                        <div className="showInfo" onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Missing Number</h1>

                                <p>
                                    Strengthen your child’s understanding of number order with this engaging <strong>Missing Number</strong> worksheet. Designed for <strong>nursery and preschool learners</strong>, this activity teaches children how to complete number sequences by identifying the number that’s missing.
                                </p>

                                <h2>🔍 What Does the Worksheet Include?</h2>
                                <p>
                                    Each row in this worksheet contains a simple number line with one number left blank. Your child must <strong>look at the sequence</strong> and fill in the missing number. This helps reinforce number recognition, sequencing, and early problem-solving skills.
                                </p>

                                <h2>🎯 Learning Goals</h2>
                                <ul>
                                    <li>Recognize number patterns and sequences</li>
                                    <li>Understand the concept of “before” and “after”</li>
                                    <li>Fill in missing numbers from 1 to 20</li>
                                    <li>Improve critical thinking and early math fluency</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/mathematics/count-object"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Count Object</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/match-object-with-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Object With Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/circle-correct-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Circle Correct Number</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why Is This Important?</h2>
                                <p>
                                    Understanding sequences is a key building block in early mathematics. It supports <strong>logical thinking</strong>, prepares children for addition and subtraction, and builds number fluency — all critical for primary school readiness.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Before / After / Between Numbers</h1>
                            <p>
                                Learning numbers is fun! In this activity, children will understand what comes <strong>before</strong>, what comes <strong>after</strong>, and what number lies <strong>between</strong> two numbers. These concepts are essential for building early math skills in nursery-level students.
                            </p>

                            <h2>📚 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Understanding number order from 1 to 20</li>
                                <li>Identifying the number that comes before or after a given number</li>
                                <li>Recognizing the number that comes between two numbers</li>
                            </ul>

                            <h2>🔢 What Comes Before?</h2>
                            <p>
                                Let’s help children learn what number comes <strong>just before</strong> a given number.
                            </p>
                            <p><em>Example:</em> What comes before 6? → <strong>5</strong></p>

                            <h2>🔢 What Comes After?</h2>
                            <p>
                                Now we find what number comes <strong>just after</strong> a given number.
                            </p>
                            <p><em>Example:</em> What comes after 3? → <strong>4</strong></p>

                            <h2>🔢 What Comes Between?</h2>
                            <p>
                                Finding the number that is <strong>in between</strong> two numbers.
                            </p>
                            <p><em>Example:</em> What comes between 7 and 9? → <strong>8</strong></p>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/mathematics/count-object"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Count Object</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/missing-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Missing Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Bigger Or Smaller</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Worksheet Works</h2>
                            <p>
                                Visual learning is powerful for toddlers. By connecting pictures with numbers, children build a solid foundation in early numeracy. Worksheets like these enhance <strong>cognitive development</strong> and <strong>encourage independent thinking</strong>.
                            </p>

                        </article>
                        {/* <h3>Find the Missing Number</h3> */}
                        <div style={{ fontSize: "24px", marginBottom: "20px" }}>
                            {sequence.map((num, index) => (
                                <span key={index} style={{ margin: "10px", fontWeight: "bold" }}>
                                    {num === -1 ? "❓" : num}
                                </span>
                            ))}
                        </div>

                        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                            {options.map((option, index) => (
                                <div className="optionButton background-blue" style={{ minWidth: '50px' }} key={index} onClick={() => handleAnswerClick(option)}>
                                    {option}
                                </div>
                            ))}
                        </div>

                        {message && <h4 style={{ marginTop: "20px", color: message.includes("Correct") ? "green" : "red" }}>{message}</h4>}

                        <div className="btn hero-btn" onClick={generateNewQuestion} style={{ marginTop: "20px" }}>Next &gt;&gt;</div>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default MissingNumber;
